import { useEffect, useMemo, useState } from 'react';
import {
  Page, Card, IndexTable, Text, Pagination, InlineStack, BlockStack, Box,
  Spinner, Banner, Badge, TextField, Icon, Checkbox, useIndexResourceState,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useMutation, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  listWheels, getWheelStats, deleteAllWheels, pushSelectedWheels,
  type WheelListParams, type WheelInstallSpeed, type WheelSource, type WheelSortBy,
} from '../api/wheels';
import { syncShopifyFitment, type SyncResponse } from '../api/shopify-sync';
import { triggerVendorPull, triggerVisionCatalogPull, triggerVisionInventoryPull, type VendorPullOption, type VisionPullSummary } from '../api/imports';
import { useUrlState, readArray, writeArray, readString, writeString, readNumber, writeNumber, readBool, writeBool } from '../lib/useUrlState';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { RangeFilter } from '../components/RangeFilter';
import { SortableHeader } from '../components/SortableHeader';
import { StatBadgeRow, type StatBlock } from '../components/StatBadgeRow';
import { FilterChips, type ActiveChip } from '../components/FilterChips';

const PAGE_SIZE = 50;

interface WheelsFilterState {
  q: string;
  brand: string[];
  source: WheelSource[];
  install: WheelInstallSpeed[];
  diameter_min?: number;
  diameter_max?: number;
  needs_review: boolean;
  include_inactive: boolean;
  sort_by: WheelSortBy;
  sort_dir: 'asc' | 'desc';
  offset: number;
}

// Codec: filter state ↔ URL search params. One source of truth — the URL.
const wheelsCodec = {
  read: (p: URLSearchParams): WheelsFilterState => ({
    q: readString(p, 'q'),
    brand: readArray(p, 'brand'),
    source: readArray(p, 'source') as WheelSource[],
    install: readArray(p, 'install') as WheelInstallSpeed[],
    diameter_min: readNumber(p, 'd_min'),
    diameter_max: readNumber(p, 'd_max'),
    needs_review: readBool(p, 'needs_review'),
    include_inactive: readBool(p, 'inactive'),
    sort_by: ((p.get('sort') ?? 'brand') as WheelSortBy),
    sort_dir: ((p.get('dir') ?? 'asc') as 'asc' | 'desc'),
    offset: readNumber(p, 'page') !== undefined ? (readNumber(p, 'page')! - 1) * PAGE_SIZE : 0,
  }),
  write: (s: WheelsFilterState, p: URLSearchParams) => {
    writeString(p, 'q', s.q);
    writeArray(p, 'brand', s.brand);
    writeArray(p, 'source', s.source);
    writeArray(p, 'install', s.install);
    writeNumber(p, 'd_min', s.diameter_min);
    writeNumber(p, 'd_max', s.diameter_max);
    writeBool(p, 'needs_review', s.needs_review);
    writeBool(p, 'inactive', s.include_inactive);
    if (s.sort_by !== 'brand') p.set('sort', s.sort_by); else p.delete('sort');
    if (s.sort_dir !== 'asc') p.set('dir', s.sort_dir); else p.delete('dir');
    if (s.offset > 0) p.set('page', String(Math.floor(s.offset / PAGE_SIZE) + 1));
    else p.delete('page');
  },
};

const SOURCE_OPTIONS: WheelSource[] = ['ATD', 'WheelPros', 'Vision', 'Manual', 'Other'];

const INSTALL_OPTIONS: { value: WheelInstallSpeed; label: string }[] = [
  { value: 'same_day',     label: 'Same day' },
  { value: 'next_day',     label: '1-3 days out' },
  { value: 'standard',     label: '3-5 days out' },
  { value: 'estimated',    label: 'Estimated' },
  { value: 'out_of_stock', label: 'Out of stock' },
];

export function WheelsList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState<WheelsFilterState>(wheelsCodec);

  // Local search input — debounced before it gets pushed to URL state so
  // typing doesn't refire the API call on every keystroke.
  const [searchInput, setSearchInput] = useState(filters.q);
  // Re-sync local input when URL state changes externally (chip removal).
  useEffect(() => { setSearchInput(filters.q); }, [filters.q]);
  const debouncedSearch = useDebouncedValue(searchInput, 250);
  useEffect(() => {
    if (debouncedSearch !== filters.q) {
      setFilters((prev) => ({ ...prev, q: debouncedSearch, offset: 0 }));
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useQuery({ queryKey: ['wheels-stats'], queryFn: getWheelStats, staleTime: 60_000 });

  const params: WheelListParams = {
    q: filters.q || undefined,
    brand: filters.brand.length ? filters.brand : undefined,
    source_guide: filters.source.length ? filters.source : undefined,
    install_speed: filters.install.length ? filters.install : undefined,
    diameter_min: filters.diameter_min,
    diameter_max: filters.diameter_max,
    needs_review: filters.needs_review || undefined,
    include_inactive: filters.include_inactive || undefined,
    sort_by: filters.sort_by,
    sort_dir: filters.sort_dir,
    limit: PAGE_SIZE,
    offset: filters.offset,
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['wheels', params],
    queryFn: () => listWheels(params),
    placeholderData: keepPreviousData,
  });

  const wheels = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  // IndexTable selection: lets the operator pick rows to push.
  const wheelsForSelection = useMemo(
    () => wheels.map((w) => ({ id: String(w.id) })),
    [wheels],
  );
  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
    clearSelection,
  } = useIndexResourceState(wheelsForSelection);

  const pushSelected = useMutation({
    mutationFn: (ids: number[]) => pushSelectedWheels(ids),
    onSuccess: (r) => {
      alert(
        `Push started — ECS task ${r.task_id}.\n\n${r.id_count} row IDs queued. The push runs ` +
        `with --only-create-missing so existing Shopify products will not be modified.\n\n` +
        `Watch progress in CloudWatch (RoadRunnerFitment-WorkerLog…).`
      );
      clearSelection();
    },
    onError: (e: Error) => alert(`Push failed: ${e.message}`),
  });

  // Stat-block configuration. Clicking toggles the relevant filter on/off
  // (clearing the same value if already active).
  const installCounts = stats.data?.by_install_speed;
  const sourceCounts: Record<string, number> = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of stats.data?.by_source ?? []) m[s.source_guide] = s.count;
    return m;
  }, [stats.data]);

  const toggleInstall = (v: WheelInstallSpeed) => {
    setFilters((p) => ({
      ...p,
      install: p.install.includes(v) ? p.install.filter((x) => x !== v) : [v],
      offset: 0,
    }));
  };
  const toggleSource = (v: WheelSource) => {
    setFilters((p) => ({
      ...p,
      source: p.source.includes(v) ? p.source.filter((x) => x !== v) : [v],
      offset: 0,
    }));
  };
  const toggleNeedsReview = () => {
    setFilters((p) => ({ ...p, needs_review: !p.needs_review, offset: 0 }));
  };

  const installBlocks: StatBlock[] = installCounts ? [
    { id: 'same_day',     label: 'Same day',     count: installCounts.same_day,     tone: 'success',   active: filters.install.includes('same_day'),     onClick: () => toggleInstall('same_day') },
    { id: 'next_day',     label: '1-3 days out', count: installCounts.next_day,     tone: 'info',      active: filters.install.includes('next_day'),     onClick: () => toggleInstall('next_day') },
    { id: 'standard',     label: '3-5 days out', count: installCounts.standard,     tone: 'new',       active: filters.install.includes('standard'),     onClick: () => toggleInstall('standard') },
    { id: 'estimated',    label: 'Estimated',    count: installCounts.estimated,    tone: 'attention', active: filters.install.includes('estimated'),    onClick: () => toggleInstall('estimated') },
    { id: 'out_of_stock', label: 'Out of stock', count: installCounts.out_of_stock, tone: 'critical',  active: filters.install.includes('out_of_stock'), onClick: () => toggleInstall('out_of_stock') },
  ] : [];

  const sourceBlocks: StatBlock[] = stats.data ? [
    { id: 'ATD',       label: 'ATD',       count: sourceCounts.ATD ?? 0,       tone: 'info', active: filters.source.includes('ATD'),       onClick: () => toggleSource('ATD') },
    { id: 'WheelPros', label: 'WheelPros', count: sourceCounts.WheelPros ?? 0, tone: 'info', active: filters.source.includes('WheelPros'), onClick: () => toggleSource('WheelPros') },
    { id: 'Vision',    label: 'Vision',    count: sourceCounts.Vision ?? 0,    tone: 'info', active: filters.source.includes('Vision'),    onClick: () => toggleSource('Vision') },
  ] : [];

  // Listing dashboard — counts at the BMF-group level (= Shopify product
  // count). "Total wheel rows" lives in the page header (49,196 records).
  // Three cards here:
  //   - In Shopify: distinct BMF group_keys with status='pushed' in
  //     shopify_push_state. This is the actual Shopify product count.
  //   - Ready to push: distinct BMF groups that pass shopify_ready_status
  //     but aren't yet in Shopify.
  //   - Blocked: groups marked excluded/blocked + groups missing all images.
  const launchBlocks: StatBlock[] = stats.data ? [
    { id: 'already_pushed', label: 'In Shopify',   count: stats.data.already_pushed_groups ?? stats.data.already_pushed ?? 0, tone: 'success',   active: false, onClick: () => setFilters((p) => ({ ...p, offset: 0 })) },
    { id: 'pending_push',   label: 'Ready to push',count: stats.data.pending_push_groups ?? stats.data.pending_push ?? 0,    tone: 'attention', active: false, onClick: () => setFilters((p) => ({ ...p, offset: 0 })) },
    { id: 'blocked',        label: 'Blocked',      count: (stats.data.blocked_groups ?? 0) + (stats.data.image_missing_groups ?? 0),         tone: 'critical',  active: false, onClick: () => setFilters((p) => ({ ...p, offset: 0 })) },
  ] : [];

  // Active filter chips — one chip per concrete value so admin can remove
  // a single brand without clearing the whole brand multi-select.
  const chips: ActiveChip[] = useMemo(() => {
    const out: ActiveChip[] = [];
    for (const v of filters.brand) out.push({
      id: `brand:${v}`, label: `Brand: ${v}`,
      onRemove: () => setFilters((p) => ({ ...p, brand: p.brand.filter((x) => x !== v), offset: 0 })),
    });
    for (const v of filters.source) out.push({
      id: `source:${v}`, label: `Source: ${v}`,
      onRemove: () => setFilters((p) => ({ ...p, source: p.source.filter((x) => x !== v), offset: 0 })),
    });
    for (const v of filters.install) {
      const lbl = INSTALL_OPTIONS.find((o) => o.value === v)?.label ?? v;
      out.push({
        id: `install:${v}`, label: `Install: ${lbl}`,
        onRemove: () => setFilters((p) => ({ ...p, install: p.install.filter((x) => x !== v), offset: 0 })),
      });
    }
    if (filters.diameter_min !== undefined || filters.diameter_max !== undefined) {
      out.push({
        id: 'diameter',
        label: `Diameter: ${filters.diameter_min ?? ''}″–${filters.diameter_max ?? ''}″`,
        onRemove: () => setFilters((p) => ({ ...p, diameter_min: undefined, diameter_max: undefined, offset: 0 })),
      });
    }
    if (filters.needs_review) {
      out.push({
        id: 'needs_review', label: 'Needs review',
        onRemove: () => setFilters((p) => ({ ...p, needs_review: false, offset: 0 })),
      });
    }
    if (filters.include_inactive) {
      out.push({
        id: 'inactive', label: 'Including inactive',
        onRemove: () => setFilters((p) => ({ ...p, include_inactive: false, offset: 0 })),
      });
    }
    return out;
  }, [filters, setFilters]);

  const clearAll = () => {
    setSearchInput('');
    setFilters({
      q: '', brand: [], source: [], install: [],
      diameter_min: undefined, diameter_max: undefined,
      needs_review: false, include_inactive: false,
      sort_by: 'brand', sort_dir: 'asc', offset: 0,
    });
  };

  const onSortChange = (next: { key: string; dir: 'asc' | 'desc' }) => {
    setFilters((p) => ({ ...p, sort_by: next.key as WheelSortBy, sort_dir: next.dir, offset: 0 }));
  };

  // Vendor pull mutations (unchanged from before)
  const syncAll = useMutation<SyncResponse, Error, void>({
    mutationFn: async () => syncShopifyFitment('all', { force: false }),
  });
  const pullVendor = useMutation<{ id: number; vendor: VendorPullOption }, Error, VendorPullOption>({
    mutationFn: (vendor) => triggerVendorPull(vendor),
  });
  const pullVisionCatalog = useMutation<VisionPullSummary, Error, void>({
    mutationFn: () => triggerVisionCatalogPull(),
  });
  const pullVisionInventory = useMutation<VisionPullSummary, Error, void>({
    mutationFn: () => triggerVisionInventoryPull(),
  });
  const deleteAll = useMutation({ mutationFn: deleteAllWheels });

  const brandOptions = (stats.data?.brands ?? [])
    .slice(0, 200)
    .map((b) => ({ value: b.brand, label: b.brand, count: b.count }));

  return (
    <Page
      title="Wheels"
      subtitle={`${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
      primaryAction={
        selectedResources.length > 0
          ? {
              content: pushSelected.isPending
                ? `Pushing ${selectedResources.length}…`
                : `Push ${selectedResources.length} to Shopify (DRAFT)`,
              loading: pushSelected.isPending,
              onAction: () => {
                if (selectedResources.length > 200) {
                  alert(`Too many selected (${selectedResources.length}). Max 200 per push.`);
                  return;
                }
                if (!confirm(
                  `Push ${selectedResources.length} selected wheel rows to Shopify as DRAFT?\n\n` +
                  `Safety: --only-create-missing is always passed, so any handle that already exists ` +
                  `in Shopify will be SKIPPED (no destructive update). DRAFT-only — no products will go live.`
                )) return;
                pushSelected.mutate(selectedResources.map((s) => Number(s)));
              },
            }
          : {
              content: syncAll.isPending ? 'Syncing…' : 'Re-sync Shopify fitment',
              loading: syncAll.isPending,
              onAction: () => {
                if (!confirm('Re-sync fitment metafields on every active wheel? Unchanged wheels are skipped.')) return;
                syncAll.mutate();
              },
            }
      }
      secondaryActions={[
        {
          content: 'Pull fresh from ATD',
          loading: pullVendor.isPending && pullVendor.variables === 'ATD',
          onAction: () => {
            if (!confirm('Queue a fresh pull of the ATD wheel feed? This enqueues an import job — it runs asynchronously.')) return;
            pullVendor.mutate('ATD', { onSuccess: (r) => navigate(`/imports/${r.id}`) });
          },
        },
        {
          content: 'Pull fresh from Wheel Pros',
          loading: pullVendor.isPending && pullVendor.variables === 'WheelPros',
          onAction: () => {
            if (!confirm('Queue a fresh pull of the Wheel Pros feed? This enqueues an import job — it runs asynchronously.')) return;
            pullVendor.mutate('WheelPros', { onSuccess: (r) => navigate(`/imports/${r.id}`) });
          },
        },
        {
          content: 'Pull fresh from Vision (catalog)',
          loading: pullVisionCatalog.isPending,
          onAction: () => {
            if (!confirm('Pull Vision MSRP.csv now? Runs synchronously (~30 sec). Honors If-Modified-Since — returns 304 instantly when the feed is unchanged.')) return;
            pullVisionCatalog.mutate(undefined, {
              onSuccess: (r) => alert(
                r.status === 304
                  ? 'Vision catalog unchanged since last pull (304 Not Modified). Nothing to do.'
                  : `Vision catalog pull complete.\n\nRaw rows: ${r.raw_rows}\nAccepted: ${r.accepted_rows}\nRejected: ${r.rejected_rows}\nInserted: ${r.upsert_inserted}\nUpdated: ${r.upsert_updated}\nBolt-pattern child rows: ${r.upsert_bolt_patterns}\n\nRejected by reason:\n${Object.entries(r.rejected_by_reason).map(([k, v]) => `  ${k}: ${v}`).join('\n') || '  (none)'}`
              ),
              onError: (e) => alert(`Vision catalog pull failed: ${e.message}`),
            });
          },
        },
        {
          content: 'Pull Vision inventory (delta)',
          loading: pullVisionInventory.isPending,
          onAction: () => {
            if (!confirm('Pull Vision inv-update.csv now? Updates only qty_* columns on existing Vision rows. ~5 sec.')) return;
            pullVisionInventory.mutate(undefined, {
              onSuccess: (r) => alert(
                r.status === 304
                  ? 'Vision inventory unchanged since last pull (304 Not Modified).'
                  : `Vision inventory pull complete. ${r.upsert_updated} rows updated, ${r.rejected_by_reason.no_match_inv_update ?? 0} skipped (SKU not in catalog yet).`
              ),
              onError: (e) => alert(`Vision inventory pull failed: ${e.message}`),
            });
          },
        },
        {
          content: 'Delete ALL wheels',
          destructive: true,
          loading: deleteAll.isPending,
          onAction: () => {
            if (!confirm('Delete EVERY wheel, every bolt-pattern row, and every fitment match? This cannot be undone. Continue?')) return;
            const typed = prompt('Type DELETE to confirm the full wipe:');
            if (typed !== 'DELETE') return;
            deleteAll.mutate(undefined, {
              onSuccess: (r) => alert(`Wiped: ${r.deleted.wheels} wheels, ${r.deleted.bolt_patterns} bolt patterns, ${r.deleted.fitment_matches} matches`),
              onError: (err) => alert(`Delete failed: ${err.message}`),
            });
          },
        },
      ]}
    >
      {/* Launch dashboard — Go-Live Ready / Pending Push / Already on Shopify */}
      {launchBlocks.length > 0 && (
        <Box paddingBlockEnd="400">
          <StatBadgeRow blocks={launchBlocks} />
        </Box>
      )}

      {/* Source breakdown — clickable */}
      {sourceBlocks.length > 0 && (
        <Box paddingBlockEnd="400">
          <StatBadgeRow blocks={sourceBlocks} />
        </Box>
      )}

      {/* Install-speed strip — clickable */}
      {installBlocks.length > 0 && (
        <Box paddingBlockEnd="400">
          <StatBadgeRow blocks={installBlocks} />
        </Box>
      )}

      {syncAll.data && (
        <Box paddingBlockEnd="400">
          <Banner tone={syncAll.data.summary.errors > 0 ? 'warning' : 'success'}>
            Sync complete — written: {syncAll.data.summary.written}, unchanged: {syncAll.data.summary.unchanged},
            skipped: {syncAll.data.summary.skipped}, errors: {syncAll.data.summary.errors}
          </Banner>
        </Box>
      )}
      {syncAll.error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" title="Sync failed">{(syncAll.error as Error).message}</Banner>
        </Box>
      )}
      {error && (
        <Box paddingBlockEnd="400">
          <Banner tone="critical" title="Failed to load wheels">{(error as Error).message}</Banner>
        </Box>
      )}

      <Card padding="0">
        <Box padding="400">
          <BlockStack gap="300">
            {/* Always-visible filter bar — search + multi-selects + range */}
            <InlineStack gap="200" wrap blockAlign="center">
              <Box minWidth="320px">
                <TextField
                  label=""
                  labelHidden
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder="Search SKU, brand, model, size (e.g. 20x9 or 6x139.7)…"
                  prefix={<Icon source={SearchIcon} />}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearchInput('')}
                />
              </Box>
              <MultiSelectFilter
                label="Brand"
                searchable
                searchPlaceholder="Search brands…"
                selected={filters.brand}
                options={brandOptions}
                onChange={(next) => setFilters((p) => ({ ...p, brand: next, offset: 0 }))}
              />
              <MultiSelectFilter
                label="Source"
                selected={filters.source}
                options={SOURCE_OPTIONS.map((s) => ({
                  value: s, label: s, count: sourceCounts[s],
                }))}
                onChange={(next) => setFilters((p) => ({ ...p, source: next as WheelSource[], offset: 0 }))}
              />
              <MultiSelectFilter
                label="Install"
                selected={filters.install}
                options={INSTALL_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                  count: installCounts ? (installCounts as any)[o.value] : undefined,
                }))}
                onChange={(next) => setFilters((p) => ({ ...p, install: next as WheelInstallSpeed[], offset: 0 }))}
              />
              <RangeFilter
                label="Diameter"
                unit={'"'}
                min={filters.diameter_min}
                max={filters.diameter_max}
                onChange={({ min, max }) => setFilters((p) => ({ ...p, diameter_min: min, diameter_max: max, offset: 0 }))}
              />
              <Checkbox
                label="Show inactive"
                checked={filters.include_inactive}
                onChange={(v) => setFilters((p) => ({ ...p, include_inactive: v, offset: 0 }))}
              />
            </InlineStack>

            <FilterChips chips={chips} onClearAll={clearAll} />
          </BlockStack>
        </Box>

        {(isLoading && !data) ? (
          <Box padding="600"><InlineStack align="center"><Spinner /></InlineStack></Box>
        ) : (
          <IndexTable
            resourceName={{ singular: 'wheel', plural: 'wheels' }}
            itemCount={wheels.length}
            selectable={true}
            selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
            onSelectionChange={handleSelectionChange}
            loading={isFetching && !!data}
            headings={[
              { title: 'ID' },
              { title: <SortableHeader label="Brand"   sortKey="brand"   activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: <SortableHeader label="Model"   sortKey="model"   activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: 'Source' },
              { title: <SortableHeader label="SKU"     sortKey="sku"     activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: 'Mfg #' },
              { title: <SortableHeader label="Size"    sortKey="size"    activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: 'Offset' },
              { title: 'BP' },
              { title: <SortableHeader label="Qty"     sortKey="qty"     activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: <SortableHeader label="Install" sortKey="install" activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: <SortableHeader label="Sell"    sortKey="price"   activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
              { title: 'MAP' },
              { title: 'Retail' },
              { title: 'Cost' },
              { title: 'Price src' },
              { title: 'Last feed' },
              { title: 'Flags' },
              { title: 'Active' },
            ]}
          >
            {wheels.map((w, i) => {
              const bps = w.bolt_patterns_canonical ?? [];
              const bp1 = bps[0] ?? '';
              const bp2 = bps[1] ?? '';
              const fmt = (n: number | string) => {
                const v = Number(n);
                return Number.isInteger(v) ? String(v) : String(v);
              };
              const size = `${fmt(w.diameter_in)}x${fmt(w.width_in)}`;
              const widthNum = Number(w.width_in);
              const isDually = w.is_dually || (Math.abs(widthNum - Math.round(widthNum * 4) / 4) < 0.01 && ((widthNum * 100) % 100 === 25));
              return (
                <IndexTable.Row
                  id={String(w.id)}
                  key={w.id}
                  position={i}
                  selected={selectedResources.includes(String(w.id))}
                  onClick={() => navigate(`/wheels/${w.id}`)}
                >
                  <IndexTable.Cell>{w.id}</IndexTable.Cell>
                  <IndexTable.Cell>{w.brand}</IndexTable.Cell>
                  <IndexTable.Cell>{w.model}</IndexTable.Cell>
                  <IndexTable.Cell>{w.source_guide ?? '—'}</IndexTable.Cell>
                  <IndexTable.Cell><Text as="span" variant="bodySm" tone="subdued">{w.sku}</Text></IndexTable.Cell>
                  <IndexTable.Cell><Text as="span" variant="bodySm" tone="subdued">{w.mfg_part_number ?? '—'}</Text></IndexTable.Cell>
                  <IndexTable.Cell>{size}</IndexTable.Cell>
                  <IndexTable.Cell>{w.offset_mm > 0 ? `+${w.offset_mm}` : w.offset_mm}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="100" wrap>
                      {bp1 && <Badge tone="info">{bp1}</Badge>}
                      {bp2 && <Badge tone="info">{bp2}</Badge>}
                      {!bp1 && '—'}
                    </InlineStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{wheelDisplayQty(w)}</IndexTable.Cell>
                  <IndexTable.Cell><InstallSpeedBadge
                    speed={w.install_speed}
                    source={w.source_guide}
                    fastestWarehouse={w.fastest_available_warehouse}
                    arrivalDays={w.estimated_arrival_days}
                    inventoryStatus={w.inventory_status}
                    inventoryAgeHours={w.inventory_age_hours}
                  /></IndexTable.Cell>
                  <IndexTable.Cell>{dollars(w.effective_price_cents)}</IndexTable.Cell>
                  <IndexTable.Cell>{dollars(w.map_price_cents)}</IndexTable.Cell>
                  <IndexTable.Cell>{dollars(w.retail_cents)}</IndexTable.Cell>
                  <IndexTable.Cell>{dollars(w.cost_cents)}</IndexTable.Cell>
                  <IndexTable.Cell>{priceSourceBadge(w.price_source)}</IndexTable.Cell>
                  <IndexTable.Cell>{ago(w.last_vendor_feed_at)}</IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack gap="100" wrap>
                      {isDually && <Badge tone="warning">Dually</Badge>}
                      {w.is_beadlock && <Badge>Beadlock</Badge>}
                      {w.dual_drilled_flag && <Badge tone="info">Dual-drilled</Badge>}
                      {!hasImage(w) && <Badge tone="critical">No image</Badge>}
                    </InlineStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {w.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="critical">Inactive</Badge>}
                  </IndexTable.Cell>
                </IndexTable.Row>
              );
            })}
          </IndexTable>
        )}

        <Box padding="400" borderBlockStartWidth="025" borderColor="border">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              {total === 0 ? '0 of 0' : `${filters.offset + 1}–${Math.min(filters.offset + PAGE_SIZE, total)} of ${total.toLocaleString()}`}
            </Text>
            <Pagination
              hasPrevious={filters.offset > 0}
              onPrevious={() => setFilters((p) => ({ ...p, offset: Math.max(0, p.offset - PAGE_SIZE) }))}
              hasNext={filters.offset + PAGE_SIZE < total}
              onNext={() => setFilters((p) => ({ ...p, offset: p.offset + PAGE_SIZE }))}
            />
          </InlineStack>
        </Box>
      </Card>
    </Page>
  );
}

// ── Shared formatters — also used by WheelDetail via re-import ──────────────

export function dollars(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export function qtyCell(q: number | null | undefined) {
  if (q === null || q === undefined) return <Text as="span" tone="subdued">—</Text>;
  if (q === 0) return <Badge tone="critical">0</Badge>;
  if (q < 5) return <Badge tone="warning">{String(q)}</Badge>;
  return <>{q}</>;
}

/**
 * Quantity display preference for wheel rows.
 *
 * Different vendors populate different qty fields:
 *   ATD / WheelPros → qty_local / qty_local_plus / qty_nationwide
 *   Vision          → qty_al / qty_nc / qty_in / qty_tx / qty_ca / qty_wa (qty_nj excluded from priority)
 *   any vendor      → quantity_on_hand (aggregate)
 *
 * Sums whichever bucket family is populated; falls back to
 * quantity_on_hand if everything else is null. When inventory_status
 * is stale or unknown the cell shows a subdued "—" so a "0" or "5"
 * never reads as authoritative.
 */
export function wheelDisplayQty(w: {
  qty_local?: number | null;
  qty_local_plus?: number | null;
  qty_nationwide?: number | null;
  qty_al?: number | null;
  qty_nc?: number | null;
  qty_in?: number | null;
  qty_tx?: number | null;
  qty_ca?: number | null;
  qty_wa?: number | null;
  qty_nj?: number | null;
  quantity_on_hand?: number | null;
  inventory_status?: 'fresh_in_stock' | 'fresh_oos' | 'unknown' | 'stale' | null;
}) {
  if (w.inventory_status === 'stale' || w.inventory_status === 'unknown') {
    return <Text as="span" tone="subdued">—</Text>;
  }
  const atdBucketsPresent =
    w.qty_local != null || w.qty_local_plus != null || w.qty_nationwide != null;
  const visionBucketsPresent =
    w.qty_al != null || w.qty_nc != null || w.qty_in != null ||
    w.qty_tx != null || w.qty_ca != null || w.qty_wa != null;
  if (atdBucketsPresent || visionBucketsPresent) {
    const total =
      (w.qty_local ?? 0) + (w.qty_local_plus ?? 0) + (w.qty_nationwide ?? 0) +
      (w.qty_al ?? 0) + (w.qty_nc ?? 0) + (w.qty_in ?? 0) +
      (w.qty_tx ?? 0) + (w.qty_ca ?? 0) + (w.qty_wa ?? 0);
    return qtyCell(total);
  }
  return qtyCell(w.quantity_on_hand);
}

export function priceSourceBadge(source: string | null | undefined) {
  if (!source) return <Text as="span" tone="subdued">—</Text>;
  switch (source) {
    case 'map':         return <Badge tone="success">MAP</Badge>;
    case 'retail':      return <Badge tone="info">Retail</Badge>;
    case 'cost_markup': return <Badge tone="warning">Cost×markup</Badge>;
    case 'manual':      return <Badge>Manual</Badge>;
    default:            return <Badge>{source}</Badge>;
  }
}

export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.round((now - then) / 60000);
  if (diffMin < 0) return 'future?';
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 48) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 14) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function hasImage(w: { image_large?: string | null; image_small?: string | null; image_thumbnail?: string | null }): boolean {
  return !!(w.image_large?.trim() || w.image_small?.trim() || w.image_thumbnail?.trim());
}

/**
 * Install-speed badge with vendor-aware labels.
 *
 * Vision Wheel ships from six US warehouses; UPS Ground transit days to
 * Marietta GA 30062 are confirmed against UPS's published service map:
 *   Decatur AL    → 1-2 days
 *   Charlotte NC  → 1-2 days
 *   Portage IN    → 3 days
 *   Midlothian TX → 3 days
 *   Corona CA     → 5 days
 *   Lacey WA      → 5 days
 * The Vision pull writes fastest_available_warehouse + estimated_arrival_days
 * onto each wheel_products row using a priority chain (AL > NC > IN/TX > CA/WA).
 * The badge prefers those derived fields when available, falling back to
 * the install_speed enum if the row is older than mig 034.
 *
 * ATD's install_speed values come from the SOAP rollup buckets
 * (Local / Local Plus / Nationwide) without per-warehouse detail —
 * the generic "1-3 / 3-5" labels stay because we genuinely don't have
 * per-DC ETAs for ATD.
 */
export function InstallSpeedBadge({
  speed, source, fastestWarehouse, arrivalDays,
  inventoryStatus, inventoryAgeHours,
}: {
  speed?: string | null;
  source?: string | null;
  fastestWarehouse?: string | null;
  arrivalDays?: string | null;
  inventoryStatus?: 'fresh_in_stock' | 'fresh_oos' | 'unknown' | 'stale' | null;
  inventoryAgeHours?: number | null;
}) {
  // Inventory-status guards. The API computes inventory_status at read
  // time so we can render distinct admin-visible states without
  // mutating any DB row.
  if (inventoryStatus === 'stale') {
    const age = inventoryAgeHours == null ? '?' :
      inventoryAgeHours >= 48 ? `${Math.round(inventoryAgeHours / 24)}d` :
      `${Math.round(inventoryAgeHours)}h`;
    return <Badge tone="warning">{`Stale inventory (${age} ago)`}</Badge>;
  }
  if (inventoryStatus === 'unknown') return <Badge>Inventory unknown</Badge>;
  if (inventoryStatus === 'fresh_oos') return <Badge tone="critical">Out of stock</Badge>;
  if (!speed) return <Text as="span" tone="subdued">—</Text>;
  if (source === 'Vision') {
    // Prefer the derived fields when present — they carry the
    // warehouse city which the badge surfaces in parens.
    if (arrivalDays && fastestWarehouse) {
      const tone =
        arrivalDays === '1-2' ? 'info' :
        arrivalDays === '3'   ? undefined :
        arrivalDays === '5'   ? 'attention' :
        undefined;
      return <Badge tone={tone}>{`${arrivalDays} days out (${shortenWarehouseLabel(fastestWarehouse)})`}</Badge>;
    }
    switch (speed) {
      case 'same_day':     return <Badge tone="success">Same day</Badge>;
      case 'next_day':     return <Badge tone="info">1-2 days out</Badge>;
      case 'standard':     return <Badge>3 days out</Badge>;
      case 'estimated':    return <Badge tone="attention">5 days out</Badge>;
      case 'out_of_stock': return <Badge tone="critical">Special order</Badge>;
      default:             return <Badge>{speed}</Badge>;
    }
  }
  switch (speed) {
    case 'same_day':     return <Badge tone="success">Same day</Badge>;
    case 'next_day':     return <Badge tone="info">1-3 days out</Badge>;
    case 'standard':     return <Badge>3-5 days out</Badge>;
    case 'estimated':    return <Badge tone="attention">Estimated</Badge>;
    case 'out_of_stock': return <Badge tone="critical">Out of stock</Badge>;
    default:             return <Badge>{speed}</Badge>;
  }
}

// "Decatur, AL" → "AL"; "Charlotte, NC" → "NC". Keeps the badge tight
// in the table while the WheelDetail page shows the full city.
function shortenWarehouseLabel(label: string): string {
  const m = label.match(/,\s*([A-Z]{2})\s*$/);
  return m ? m[1] : label;
}
